import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { InfoOutlined } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";

import messages from "../../../assets/locale/messages";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import Select from "../../../components/Select";
import Tooltip from "../../../components/Tooltip";
import Upload from "../../../components/Upload";
import PhoneInput from "../../../components/PhoneInput";
import {
  addEmployeeRequest,
  editEmployeeRequest,
  getEmployeeDetailsRequest
} from "../../../store/Employees/actions";
import { rolesLookupRequest } from "../../../store/Lookups/actions";
import {
  ALLOW_ALL_WITH_MANDATORY_CHARACTERS,
  EMAIL_REGEX
} from "../../../utils/Patterns";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  BLOB_CONTAINERS,
  UploadBlobToContainer
} from "../../../utils/blobStorageServices";
import { checkBackgroundImageValidity } from "../../../utils/Helpers";
import "./AddEditEmployees.scss";

const AddEditEmployee = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const uploadElementRef = useRef();
  const { id } = useParams();

  const [rolesOptions, setRolesOptions] = useState([]);
  const [countryCodeLength, setCountryCodeLength] = useState(0);
  const [countryShort, setCountryShort] = useState("");
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [isValidEmployeeImage, setIsValidEmployeeImage] =
    useState(false);
  const lang = useSelector((state) => state.locale.lang);
  const { shared, inputsValidations, employees } = messages[lang];
  const rolesLookup = useSelector(
    (state) => state.lookups.roles.list
  );
  const employeeDetails = useSelector(
    (state) => state.employees.employeeDetails
  );

  useEffect(() => {
    if (id) {
      dispatch(getEmployeeDetailsRequest(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    dispatch(rolesLookupRequest());
  }, [dispatch]);

  useEffect(() => {
    if (rolesLookup) {
      setRolesOptions([
        ...rolesLookup.map(({ Id, Name }) => ({
          id: Id,
          label: Name
        }))
      ]);
    }
  }, [rolesLookup]);

  const formik = useFormik({
    initialValues: {
      Name: "",
      Email: "",
      MobileNumber: "",
      MobileCode: "",
      Role: null,
      EmployeeImage: "",
      dbEmployeeImage: "",
      EmployeeImageInfo: {}
    },
    validationSchema: Yup.object({
      Name: Yup.string()
        .matches(
          ALLOW_ALL_WITH_MANDATORY_CHARACTERS,
          "invalidFullName"
        )
        .max(50, "employeeNameLong")
        .required("nameRequired"),
      Email: Yup.string()
        .matches(EMAIL_REGEX, "validEmailFormat")
        .required("emailRequired"),
      MobileNumber: Yup.string()
        .test(
          "valid-length",
          "employeeMobileNumberLength",
          function (value) {
            if (!!value) {
              return value.length >= 6;
            } else return true;
          }
        )
        .nullable(),
      Role: Yup.object().required("roleRequired")
    }),
    onSubmit: async ({
      Name,
      Email,
      MobileNumber,
      MobileCode,
      Role,
      EmployeeImage,
      dbEmployeeImage,
      EmployeeImageInfo
    }) => {
      let employeeFileName = "";
      if (EmployeeImage) {
        if (EmployeeImageInfo.isNew) {
          employeeFileName = await UploadBlobToContainer(
            { blob: EmployeeImage, ...EmployeeImageInfo },
            BLOB_CONTAINERS.images
          );
        } else {
          employeeFileName = EmployeeImage.split("/images/")[1];
        }
      }

      const employeeData = {
        Id: id,
        Name,
        MobileNumber,
        MobileCode,
        RoleId: Role.id,
        Image: employeeFileName || "",
        OriginalImage: EmployeeImageInfo?.name || ""
      };

      const dbData = {
        dbEmployeeImage,
        isEmployeeImageNew: EmployeeImageInfo.isNew
      };
      if (id) {
        dispatch(
          editEmployeeRequest({
            data: employeeData,
            dbData,
            navigate
          })
        );
      } else {
        employeeData.Email = Email;
        dispatch(
          addEmployeeRequest({
            data: employeeData,
            dbData,
            navigate
          })
        );
      }
    }
  });

  const {
    values,
    setFieldTouched,
    setFieldValue,
    errors,
    touched,
    handleSubmit,
    dirty,
    setValues,
    isValid
  } = formik;

  useEffect(() => {
    if (employeeDetails && Object.keys(employeeDetails).length) {
      if (id) {
        const {
          Name,
          Email,
          RoleId,
          RoleName,
          MobileNumber,
          MobileCode,
          Image,
          OriginalImage
        } = employeeDetails;

        if (Image) {
          checkBackgroundImageValidity(Image, (isValid) => {
            setIsValidEmployeeImage(isValid);
          });
        }
        setValues({
          Name,
          Email,
          Role:
            RoleId && RoleName
              ? { id: RoleId, label: RoleName }
              : null,
          MobileNumber,
          MobileCode,
          EmployeeImage: isValidEmployeeImage ? Image : null,
          dbEmployeeImage: isValidEmployeeImage ? Image : null,
          EmployeeImageInfo: { name: OriginalImage, isNew: false }
        });
      }
    }
  }, [employeeDetails, setValues, id, isValidEmployeeImage]);

  return (
    <div className="add-subcategory-container">
      <div className="page-title">
        <h3 className="fweight-700 fsize-22 mb-4 title-color">
          {id
            ? employees.addEmployee.editEmployee
            : employees.addEmployee.addEmployee}
        </h3>
      </div>
      <div className="add-edit-employee-form">
        <form noValidate onSubmit={handleSubmit}>
          <div className="main-data-section bg-white border-radius-10 p-4 mb-4">
            <h5 className="fsize-16 fweight-600 mb-4 title-color">
              {employees.addEmployee.employeeDetails}
            </h5>
            <div className="mb-4 add-edit-employee-container">
              <div className="title d-flex align-items-center justify-content-start mb-3">
                <h6 className="mb-0 me-2 ">
                  {employees.addEmployee.labels.employeeImage}
                  <span className="text-light-grey">(Optional)</span>
                </h6>
                <Tooltip
                  className="text-secondary ms-md-2"
                  title={
                    <ul className="mb-0 ps-0">
                      <li>{inputsValidations.uploadSizeError}</li>
                      <li>{inputsValidations.uploadImgSizeError}</li>
                      <li>{inputsValidations.uploadTypeError}</li>
                    </ul>
                  }
                  content={
                    <InfoOutlined className="text-secondary" />
                  }
                />
              </div>
              <div className="employee-image">
                <Upload
                  label={<div ref={uploadElementRef}></div>}
                  onChange={(img) => {
                    setFieldTouched("EmployeeImage");
                    setFieldValue("EmployeeImage", img.blob);
                    setFieldValue("EmployeeImageInfo", {
                      ...img?.imageInfo,
                      isNew: true
                    });
                  }}
                  onClear={() => {
                    setFieldTouched("EmployeeImage");
                    setFieldValue("EmployeeImage", null);
                    setFieldValue("EmployeeImageInfo", {});
                  }}
                  id="EmployeeImage"
                  name="EmployeeImage"
                  userImage={values.EmployeeImage}
                  isInputHasErr={!!errors.EmployeeImage}
                  errMsg={errors.EmployeeImage}
                  showValidationMsg={false}
                  isCropMode={true}
                  cropAspect={1 / 1}>
                  upload an image
                </Upload>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-4">
                <Input
                  required
                  inputWrapperClass="mb-4"
                  label={employees.addEmployee.labels.fullName}
                  placeholder={
                    employees.addEmployee.placeholders.name
                  }
                  onChange={(value) => {
                    if (!value || value.trim()) {
                      const modifiedValue = value.replace(
                        /\s\s+/g,
                        " "
                      );
                      if (value[0] !== " ") {
                        setFieldTouched("Name");
                        setFieldValue("Name", modifiedValue);
                      }
                    }
                  }}
                  value={values.Name}
                  isInputHasErr={!!(touched.Name && errors.Name)}
                  errMsg={errors.Name}
                  name="Name"
                  id="Name"
                />

                <PhoneInput
                  onFocus={() => {
                    setIsPhoneFocused(true);
                  }}
                  onBlur={() => {
                    setIsPhoneFocused(false);
                  }}
                  isFocused={isPhoneFocused}
                  onChange={(value, dialCodeLength) => {
                    setCountryCodeLength(dialCodeLength);

                    const number = value.slice(dialCodeLength);
                    const code = value.slice(0, dialCodeLength);

                    setFieldTouched("MobileNumber");
                    setValues({
                      ...values,
                      MobileNumber: number,
                      MobileCode: code
                    });
                  }}
                  userCountryShort={
                    !id &&
                    !isPhoneFocused &&
                    employeeDetails &&
                    !!Object.keys(employeeDetails)?.length &&
                    !employeeDetails.MobileNumber &&
                    values.MobileNumber.length === 0 &&
                    values.MobileCode
                      ? values.MobileCode
                      : ""
                  }
                  inputWrapperClass={"add-employee-form-field"}
                  key={employees.addEmployee.labels.mobileNumber}
                  label={`${employees.addEmployee.labels.mobileNumber} ${shared.optional}`}
                  labelClassName="pb-2 main-text-color"
                  placeholder={
                    employees.addEmployee.placeholders.number
                  }
                  value={`${values.MobileCode}${values.MobileNumber}`}
                  isInputHasErr={
                    !!(touched.MobileNumber && errors.MobileNumber)
                  }
                  errMsg={errors.MobileNumber}
                  name="MobileNumber"
                  id="MobileNumber"
                />
              </div>
              <div className="col-md-6">
                <Input
                  required
                  inputWrapperClass="mb-4"
                  label={employees.addEmployee.labels.emailAddress}
                  placeholder={
                    employees.addEmployee.placeholders.email
                  }
                  onChange={(value) => {
                    if (!value || value.trim()) {
                      const modifiedValue = value.replace(
                        /\s\s+/g,
                        " "
                      );
                      if (value[0] !== " ") {
                        setFieldTouched("Email");
                        setFieldValue("Email", modifiedValue);
                      }
                    }
                  }}
                  value={values.Email}
                  isInputHasErr={!!(touched.Email && errors.Email)}
                  errMsg={errors.Email}
                  name="Email"
                  id="Email"
                  disabled={!!id}
                />

                <Select
                  required
                  wrapperClass="mb-4"
                  labelClassName=""
                  label={employees.addEmployee.labels.assignToRole}
                  placeholder={
                    employees.addEmployee.placeholders.role
                  }
                  options={rolesOptions}
                  onChange={(value) => {
                    setFieldTouched("Role");
                    setFieldValue("Role", value);
                  }}
                  value={values.Role}
                  hasError={!!(touched.Role && errors.Role)}
                  errMsg={errors.Role}
                  id="Role"
                  name="Role"
                />
              </div>
            </div>
          </div>

          <div className="actions-section bg-white border-radius-10 p-4 d-flex justify-content-between align-items-center">
            <Button
              key={shared.cancel}
              outlined
              label={shared.cancel}
              onClick={() => navigate(ROUTE_PATHS.employeesList)}
              customSpacing={"px-4 px-md-5"}
            />
            <Button
              label={id ? shared.save : shared.add}
              type="submit"
              disabled={!dirty || !isValid}
              customSpacing={"px-4 px-md-5"}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditEmployee;
